import type { Address } from 'viem';
import localhostDeployment from './deployments/localhost.json';
import bscTestnetDeployment from './deployments/bscTestnet.json';

interface Deployment {
  chainId: number;
  factory: Address;
  router: Address;
  wbnb: Address;
  feeTo?: Address;
  farm?: Address;
  farmPools?: Record<string, number>;
  governor?: Address;
  timelock?: Address;
  governorDeployBlock?: number;
  seedProposal?: {
    id: string;
    description: string;
    proposer: Address;
    targets: Address[];
    values: string[];
    calldatas: `0x${string}`[];
  };
  tokens: Record<string, Address>;
  pairs: Record<string, Address>;
  saltRwa?: { token: Address; pair: Address };
  earn?: { address: Address; pools: Record<string, number> };
  launchpad?: { address: Address };
  staking?: { address: Address };
  lending?: { address: Address; collaterals: string[] };
  prediction?: { address: Address };
  bridge?: { address: Address; tokens: Record<string, { wrapped: Address }> };
}

const DEPLOYMENTS: Record<number, Deployment> = {
  31337: localhostDeployment as Deployment,
  97: bscTestnetDeployment as Deployment,
};

export const DEFAULT_CHAIN_ID = 97;

export function getDeployment(chainId?: number): Deployment | undefined {
  return DEPLOYMENTS[chainId ?? DEFAULT_CHAIN_ID];
}

export function getTokenAddress(symbol: string, chainId?: number): Address | undefined {
  return getDeployment(chainId)?.tokens[symbol];
}

export function getPairAddress(poolId: string, chainId?: number): Address | undefined {
  return getDeployment(chainId)?.pairs[poolId];
}

export function getFarmPid(poolId: string, chainId?: number): number | undefined {
  return getDeployment(chainId)?.farmPools?.[poolId];
}

export interface OnchainPair {
  address: Address;
  token0: Address;
  token1: Address;
}

function pairKey(tokenA: Address, tokenB: Address): string {
  const [a, b] = [tokenA.toLowerCase(), tokenB.toLowerCase()].sort();
  return `${a}:${b}`;
}

// Returns only direct and approved-hub paths whose every edge was enumerated
// from the Factory and verified against Factory.getPair. SwapPanel quotes all
// returned candidates with the Router and selects the best output.
export function resolveSwapPath(
  fromSymbol: string,
  toSymbol: string,
  pairs: readonly OnchainPair[],
  chainId?: number,
): Address[][] {
  const deployment = getDeployment(chainId);
  const from = deployment?.tokens[fromSymbol];
  const to = deployment?.tokens[toSymbol];
  if (!deployment || !from || !to || from.toLowerCase() === to.toLowerCase()) return [];

  const edges = new Set(pairs.map((pair) => pairKey(pair.token0, pair.token1)));
  const hasPair = (tokenA: Address, tokenB: Address) => edges.has(pairKey(tokenA, tokenB));
  const candidates: Address[][] = [];

  if (hasPair(from, to)) candidates.push([from, to]);

  const hubs = [deployment.tokens.USDT, deployment.wbnb].filter(
    (hub, index, all): hub is Address =>
      Boolean(hub) &&
      hub.toLowerCase() !== from.toLowerCase() &&
      hub.toLowerCase() !== to.toLowerCase() &&
      all.findIndex((candidate) => candidate?.toLowerCase() === hub.toLowerCase()) === index,
  );

  for (const hub of hubs) {
    if (hasPair(from, hub) && hasPair(hub, to)) candidates.push([from, hub, to]);
  }

  return candidates;
}
